import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"

/**
 * /feature-flags/{flagId} に登録される flagId の集合。
 *
 * 新規機能の段階的公開を行いたい場合は、ここに値を追加し、`FeatureFlagDocuments.flags` に
 * 表示名・説明を含むエントリを登録する。
 */
export const FeatureFlagValue = z.enum([
  "vc-vrm",
  "disable-new-subscription",
  "disable-coin-purchase",
  "end-app-mode",
])

export type FeatureFlagValue = z.infer<typeof FeatureFlagValue>

/**
 * /feature-flags/{flagId} ドキュメントのメタデータ。
 *
 * 各機能の段階的公開制御に使用する汎用的なフラグ。
 * - `public: true`  → 対象機能を公開する。実際の公開範囲（全ユーザー、プレミアム会員のみ 等）は各機能の実装側で制御する。
 * - `public: false` → 対象機能を非公開にする。`/feature-flags/{flagId}/allowed-users/{userId}` に登録された許可ユーザーのみ利用可能。
 *
 * ドキュメント自体が存在しない場合も `public: false` 扱い（= 許可ユーザーのみ）として扱う。
 * これにより、新規フラグの初期値セットを明示的に行わなくても安全側に倒れる（未公開からスタート）。
 *
 * このフラグは「公開／非公開」のみを表し、誰に対して公開するか（オーディエンス）の情報は持たない。
 * 機能ごとの追加判定（プレミアム会員チェック等）は、利用側のガード処理で行う。
 */
export const FeatureFlagDocumentMeta = z.object({
  public: z.boolean().describe("true なら対象機能を公開する。false なら内部ユーザーのみに公開する"),
}).describe("機能フラグドキュメント。/feature-flags/{flagId} に保存される。")

export type FeatureFlagDocumentMeta = z.infer<typeof FeatureFlagDocumentMeta>

export const FeatureFlagDocumentBody = z.intersection(
  FeatureFlagDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type FeatureFlagDocumentBody = z.infer<typeof FeatureFlagDocumentBody>

/**
 * 機能フラグの公開状態を更新するリクエスト（管理 API 用）。
 */
export const UpdateFeatureFlagRequest = z.object({
  public: z.boolean(),
})

export type UpdateFeatureFlagRequest = z.infer<typeof UpdateFeatureFlagRequest>

/**
 * 既知の機能フラグの定義エントリ。
 *
 * `id` は `FeatureFlagValue` で型制約されているため、ここに登録されていない flagId は
 * 利用側でコンパイルエラーになる。
 */
export type FeatureFlagDefinition = {
  /** 機能フラグID。`/feature-flags/{flagId}` の `flagId` に対応する。 */
  id: FeatureFlagValue
  /** 管理画面表示用の表示名。 */
  label: string
  /** 管理画面表示用の説明文。 */
  description: string
}

/**
 * /feature-flags/{flagId} で使用する flagId の定義。
 *
 * 新規機能の段階的公開を行いたい場合は、
 *   1. `FeatureFlagValue` enum に値を追加する
 *   2. `FeatureFlagDocuments.flags` にエントリを追加する
 */
/**
 * /feature-flags/{flagId}/allowed-users/{userId} ドキュメントのボディ。
 *
 * 機能フラグごとの「許可ユーザーリスト」を表現するサブコレクション。
 * `public: false` の機能フラグに対して、個別に利用を許可するユーザーを登録する。
 */
export const FeatureFlagAllowedUserDocumentBody = z.object({
  note: z.string().optional().describe("許可ユーザーの追加理由やメモ（任意）"),
  granted_at: CreatedAt,
})

export type FeatureFlagAllowedUserDocumentBody = z.infer<typeof FeatureFlagAllowedUserDocumentBody>

/**
 * 許可ユーザーを追加するリクエスト（管理 API 用）。
 */
export const AddFeatureFlagAllowedUserRequest = z.object({
  userId: z.string().min(1, "userId は必須です"),
  note: z.string().optional(),
})

export type AddFeatureFlagAllowedUserRequest = z.infer<typeof AddFeatureFlagAllowedUserRequest>

export class FeatureFlagDocuments {

  /** 既知の機能フラグ一覧（管理画面の一覧表示・利用側の型参照に使用）。 */
  static readonly flags: ReadonlyArray<FeatureFlagDefinition> = [
    {
      id: "vc-vrm",
      label: "VC連携VRM",
      description: "VirtualCast 連携による VRM 登録・利用機能（プレミアム会員向け）。非公開時は内部ユーザーのみ利用可能。",
    },
    {
      id: "disable-new-subscription",
      label: "新規サブスクリプション停止",
      description: "有効化すると、新規サブスクリプションの登録（reclaim）を停止する。プレミアム機能停止時に使用。",
    },
    {
      id: "disable-coin-purchase",
      label: "有償コイン販売停止",
      description: "有効化すると、ストア経由のコイン購入（reclaim）を停止する。プレミアム機能停止時に使用。",
    },
    {
      id: "end-app-mode",
      label: "アプリ終了モード（サービスAPI遮断）",
      description: "有効化すると、サービス向けREST API（requireServiceLoginHandler 等）を allowlist の一部を除いて 503 SERVICE_ENDED で遮断する。終了アプリ単体運用へ切替後に有効化する。管理API・内部API・バッチは影響を受けない。",
    },
  ]

}
